import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Link } from "wouter";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/contexts/AuthContext";
import type { Agent } from "@/types/aiToolkit";
import { BUILTIN_AGENT_SKILLS, composeSkillInstructions, getSkillsForScopes } from "@/data/agentSkills";
import { workerProfilesService } from "@/services/workerProfilesService";
import { userAgentSkillsService } from "@/services/userAgentSkillsService";
import { toolRegistry } from "@/services/toolRegistry";
import { aiAgentsService } from "@/services/aiAgentsService";
import { INTEGRATION_SCOPE_OPTIONS } from "@/lib/agentSkillConstants";
import { SaveAsSkillDialog } from "@/components/agents/SaveAsSkillDialog";
import { DEFAULT_CHAT_HANDOFF } from "@/lib/agentChatHandoff";
import type { AgentChatHandoffPermissions } from "@/types/agentHandoff";

export interface AgentWorkflowFieldsProps {
  value: Partial<Agent>;
  onChange: (patch: Partial<Agent>) => void;
}

function getChatHandoff(value: Partial<Agent>): AgentChatHandoffPermissions {
  const raw = value.actionPermissions?.chatHandoff;
  if (raw && typeof raw === "object" && "enabled" in raw) {
    return raw as AgentChatHandoffPermissions;
  }
  return { ...DEFAULT_CHAT_HANDOFF, enabled: false };
}

function patchChatHandoff(
  value: Partial<Agent>,
  patch: Partial<AgentChatHandoffPermissions>
): Partial<Agent> {
  const current = getChatHandoff(value);
  return {
    actionPermissions: {
      ...value.actionPermissions,
      chatHandoff: { ...current, ...patch },
    },
  };
}

export function AgentWorkflowFields({ value, onChange }: AgentWorkflowFieldsProps) {
  const { user } = useAuth();
  const scopes = value.integrationScopes ?? [];
  const [saveAsSkillOpen, setSaveAsSkillOpen] = useState(false);

  const { data: profiles = [] } = useQuery({
    queryKey: ["worker-profiles"],
    queryFn: () => workerProfilesService.listAll(),
  });

  const { data: userSkills = [] } = useQuery({
    queryKey: ["user-agent-skills", user?.id],
    queryFn: () => userAgentSkillsService.list(),
    enabled: !!user,
  });

  const { data: allAgents = [] } = useQuery({
    queryKey: ["/api/ai-agents", "workflow-fields"],
    queryFn: () => aiAgentsService.getAgents(),
  });

  const builtinSkills = useMemo(() => getSkillsForScopes(scopes), [scopes]);
  const tools = toolRegistry.listDefinitions();
  const chatHandoff = getChatHandoff(value);

  const preview = useMemo(() => {
    const parts = [
      value.customInstructions?.trim(),
      value.persona?.systemPrompt?.trim(),
      composeSkillInstructions(value.attachedSkillIds ?? []),
    ].filter(Boolean);
    return parts.join("\n\n");
  }, [value.customInstructions, value.persona?.systemPrompt, value.attachedSkillIds]);

  const toggleSkill = (id: string) => {
    const cur = value.attachedSkillIds ?? [];
    onChange({
      attachedSkillIds: cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id],
    });
  };

  const toggleScope = (scope: string) => {
    const cur = value.integrationScopes ?? [];
    onChange({
      integrationScopes: cur.includes(scope) ? cur.filter((x) => x !== scope) : [...cur, scope],
    });
  };

  const toggleComposed = (id: string) => {
    const cur = value.composedAgentIds ?? [];
    onChange({
      composedAgentIds: cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id],
    });
  };

  const toggleTool = (id: string) => {
    const cur = value.allowedToolIds ?? [];
    onChange({
      allowedToolIds: cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id],
    });
  };

  return (
    <div className="space-y-6">
      <div>
        <Label>Custom instructions (any instructions for the model)</Label>
        <Textarea
          className="mt-2 min-h-[100px]"
          value={value.customInstructions ?? ""}
          onChange={(e) => onChange({ customInstructions: e.target.value })}
          placeholder="Tell the agent exactly how to behave..."
        />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Button type="button" variant="outline" size="sm" onClick={() => setSaveAsSkillOpen(true)}>
          Save instructions as skill
        </Button>
        <Link href="/ai-agents/skills">
          <Button type="button" variant="ghost" size="sm">
            Manage skills
          </Button>
        </Link>
      </div>

      <div>
        <Label>Execution mode</Label>
        <Select
          value={value.executionMode ?? "manual"}
          onValueChange={(v) => onChange({ executionMode: v as Agent["executionMode"] })}
        >
          <SelectTrigger className="mt-2">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="manual">Manual only</SelectItem>
            <SelectItem value="automatic">Automatic only</SelectItem>
            <SelectItem value="both">Manual and automatic</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label>Integration scopes</Label>
        <div className="flex flex-wrap gap-2 mt-2">
          {INTEGRATION_SCOPE_OPTIONS.map((s) => (
            <Badge
              key={s}
              variant={scopes.includes(s) ? "default" : "outline"}
              className="cursor-pointer"
              onClick={() => toggleScope(s)}
            >
              {s}
            </Badge>
          ))}
        </div>
      </div>

      <div>
        <Label>AI Chat handoff</Label>
        <p className="text-xs text-muted-foreground mt-1 mb-2">
          Allow discussing agent results in AI Chat and resuming agent work afterward.
        </p>
        <label className="flex items-center gap-2 text-sm cursor-pointer mb-3">
          <Checkbox
            checked={chatHandoff.enabled}
            onCheckedChange={(checked) =>
              onChange(patchChatHandoff(value, { enabled: checked === true }))
            }
          />
          Allow discuss in AI Chat and resume agent work
        </label>
        {chatHandoff.enabled && (
          <div className="space-y-2 pl-6 border-l-2 border-muted ml-1">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <Checkbox
                checked={chatHandoff.includeSourceContent !== false}
                onCheckedChange={(checked) =>
                  onChange(patchChatHandoff(value, { includeSourceContent: checked === true }))
                }
              />
              Include source content (transcript, email body)
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <Checkbox
                checked={chatHandoff.includeAgentOutput !== false}
                onCheckedChange={(checked) =>
                  onChange(patchChatHandoff(value, { includeAgentOutput: checked === true }))
                }
              />
              Include agent output in chat context
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <Checkbox
                checked={chatHandoff.allowResume !== false}
                onCheckedChange={(checked) =>
                  onChange(patchChatHandoff(value, { allowResume: checked === true }))
                }
              />
              Allow resume agent work from chat
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <Checkbox
                checked={Boolean(chatHandoff.autoIndexForRag)}
                onCheckedChange={(checked) =>
                  onChange(patchChatHandoff(value, { autoIndexForRag: checked === true }))
                }
              />
              Auto-index for AI chat retrieval when opening chat
            </label>
          </div>
        )}
      </div>

      <div>
        <Label>Worker profile</Label>
        <Select
          value={value.workerProfileId ?? "__default__"}
          onValueChange={(v) => onChange({ workerProfileId: v === "__default__" ? null : v })}
        >
          <SelectTrigger className="mt-2">
            <SelectValue placeholder="Balanced default" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__default__">Balanced default</SelectItem>
            {profiles.map((p) => (
              <SelectItem key={p.id} value={p.id}>
                {p.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label>Built-in skills</Label>
        <div className="mt-2 space-y-2 max-h-40 overflow-y-auto">
          {builtinSkills.map((skill) => (
            <label key={skill.id} className="flex items-start gap-2 text-sm cursor-pointer">
              <Checkbox
                checked={(value.attachedSkillIds ?? []).includes(skill.id)}
                onCheckedChange={() => toggleSkill(skill.id)}
              />
              <span>
                <span className="font-medium">{skill.name}</span>
                <span className="text-muted-foreground block text-xs">{skill.description}</span>
              </span>
            </label>
          ))}
        </div>
      </div>

      <div>
        <Label>Your saved skills</Label>
        {userSkills.length === 0 ? (
          <p className="text-xs text-muted-foreground mt-2">
            No saved skills yet — duplicate a built-in on{" "}
            <Link href="/ai-agents/skills" className="underline">
              Manage skills
            </Link>
            , create one, or save from an agent.
          </p>
        ) : (
          <div className="mt-2 space-y-2">
            {userSkills.map((skill) => (
              <label key={skill.id} className="flex items-center gap-2 text-sm cursor-pointer">
                <Checkbox
                  checked={(value.attachedSkillIds ?? []).includes(skill.id)}
                  onCheckedChange={() => toggleSkill(skill.id)}
                />
                {skill.name}
              </label>
            ))}
          </div>
        )}
      </div>

      <div>
        <Label>Allowed tools</Label>
        <div className="mt-2 flex flex-wrap gap-2">
          {tools.map((t) => (
            <Badge
              key={t.id}
              variant={(value.allowedToolIds ?? []).includes(t.id) ? "default" : "outline"}
              className="cursor-pointer"
              onClick={() => toggleTool(t.id)}
            >
              {t.name}
            </Badge>
          ))}
        </div>
      </div>

      <div>
        <Label>Composite agents (building blocks)</Label>
        <div className="mt-2 space-y-1 max-h-32 overflow-y-auto">
          {allAgents
            .filter((a) => a.id !== value.id)
            .map((a) => (
              <label key={a.id} className="flex items-center gap-2 text-sm cursor-pointer">
                <Checkbox
                  checked={(value.composedAgentIds ?? []).includes(a.id)}
                  onCheckedChange={() => toggleComposed(a.id)}
                />
                {a.name}
              </label>
            ))}
        </div>
      </div>

      <div>
        <Label>Composed prompt preview</Label>
        <pre className="mt-2 text-xs bg-muted p-3 rounded-md max-h-40 overflow-auto whitespace-pre-wrap">
          {preview || "(empty)"}
        </pre>
      </div>

      <SaveAsSkillDialog
        open={saveAsSkillOpen}
        onOpenChange={setSaveAsSkillOpen}
        agent={value}
        onSkillCreated={(skillId) => {
          const cur = value.attachedSkillIds ?? [];
          if (!cur.includes(skillId)) {
            onChange({ attachedSkillIds: [...cur, skillId] });
          }
        }}
      />
    </div>
  );
}
